import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { AlertCircle } from "lucide-react";

interface MonitoringReadingsTableProps {
  profile: any;
  readings: any[];
}

export function MonitoringReadingsTable({ profile, readings }: MonitoringReadingsTableProps) {
  const parameters = (profile.parameters as any[]) || [];

  if (readings.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Reading History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No readings recorded yet. Click "Take Reading" to start tracking.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reading History ({readings.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date & Time</TableHead>
                {parameters.map((param: any) => (
                  <TableHead key={param.name}>{param.name}</TableHead>
                ))}
                <TableHead>Alerts</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {readings.map((reading) => {
                const readingData = reading.readings as any[];
                const hasAlerts = reading.alerts_triggered && (reading.alerts_triggered as any[]).length > 0;
                
                return (
                  <TableRow key={reading.id}>
                    <TableCell className="whitespace-nowrap">
                      {format(new Date(reading.recorded_at), 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                    {parameters.map((param: any) => {
                      const value = readingData?.find((r: any) => r.name === param.name);
                      const isOutOfRange = value && (value.value < param.min || value.value > param.max);
                      
                      return (
                        <TableCell key={param.name}>
                          <span className={isOutOfRange ? "text-destructive font-semibold" : ""}>
                            {value?.value || '-'} {value?.unit || param.unit}
                          </span>
                        </TableCell>
                      );
                    })}
                    <TableCell>
                      {hasAlerts ? (
                        <Badge variant="destructive" className="gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {(reading.alerts_triggered as any[]).length}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">None</Badge>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {reading.notes || '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
